<template>
  <div
    id="templateRender"
    :style="{ 'margin-top': margin + 'px' }"
    @click="iframeClick"
  >
    <t-loading
      v-show="isLoading"
      inverse
      data-testid="loader-preview"
    ></t-loading>

    <iframe
      id="previewIframe"
      style="border: none"
      :style="{
        height: fullHeight + 'px',
        width: previewWidth,
        margin: '0 auto',
      }"
      :class="{
        'opacity-30': isLoading || iframeIsLoading,
      }"
      @click="iframeClick"
      @load="onIframeLoad"
    >
    </iframe>

    <LeftPreviewPanel />
  </div>
</template>

<script lang="ts" setup>
  import LeftPreviewPanel from "./LeftPreviewPanel/LeftPreviewPanel.vue";
  import { usePreviewStore } from "@/stores/preview";
  import { useTopBarStore } from "@/stores/topBar";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import usePreview from "@/composables/usePreview";
  import { applyDarkMode } from "@/utils/convertToDarkMode";
  import { TLoading } from "@topol/twig-ui";
  import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";

  const fullHeight = ref(window.innerHeight);

  const previewStore = usePreviewStore();
  const topBarStore = useTopBarStore();
  const mjmlStore = useMjmlStore();
  const { isLoading, setPreview, getImageRatioIframe } = usePreview();

  const darkWasSet = ref(false);

  const previewWidth = computed(() => {
    return previewStore.previewSize;
  });

  const margin = computed(() => {
    return toBarMargin.value + utilityBarMargin.value;
  });

  const iframeIsLoading = computed(() => {
    return previewStore.iframeIsLoading;
  });

  const toBarMargin = computed(() => {
    if (topBarStore.removeTopBar) {
      return 0;
    } else if (!topBarStore.showPreviewTopBar && !topBarStore.showMainTopBar) {
      return 0;
    } else if (!topBarStore.showMainTopBar) {
      return 48;
    } else if (!topBarStore.showPreviewTopBar) {
      return 56;
    } else {
      return 106;
    }
  });

  const utilityBarMargin = computed(() => {
    if (topBarStore.showWindowBar) {
      return 32;
    }

    return 2;
  });

  const darkMode = computed({
    get: () => previewStore.darkMode,
    set: (value: boolean) => {
      previewStore.darkMode = value;
    },
  });

  const langToShow = computed(() => mjmlStore.langToShow);

  function handleResize() {
    fullHeight.value = window.innerHeight - margin.value;
  }

  function setHtml() {
    const iframe = document.getElementById(
      "previewIframe"
    ) as HTMLIFrameElement;
    if (typeof previewStore.previewHTML === "string" && iframe) {
      iframe.srcdoc = previewStore.previewHTML;
    }
  }

  function iframeClick() {
    window.$event.emit("iframeClick");
  }

  function onIframeLoad() {
    previewStore.iframeIsLoading = false;
    if (darkMode.value && !darkWasSet.value) {
      setDarkMode();
    }
    if (previewStore.enableImageToTextRatio) {
      getImageRatioIframe();
    }
    try {
      const iframeElement = document.getElementById("previewIframe");
      // @ts-expect-error
      if (iframeElement && iframeElement.contentWindow) {
        // @ts-expect-error
        iframeElement.contentWindow.addEventListener("click", iframeClick);
      }
    } catch (e) {
      console.error(e);
    }
  }

  function setDarkMode() {
    const previewHtmlString = previewStore.previewHTML;
    const frame = document.querySelector("#previewIframe") as HTMLIFrameElement;
    if (!previewHtmlString || frame === null || frame.contentWindow === null)
      return;

    const darkHtml = applyDarkMode(previewHtmlString);

    frame.contentWindow.document.open("text/html", "replace");
    frame.contentWindow.document.write(darkHtml);
    frame.contentWindow.document.close();
    darkWasSet.value = true;
  }

  function setLightMode() {
    const previewHtmlString = previewStore.previewHTML;
    const frame = document.querySelector("#previewIframe") as HTMLIFrameElement;
    if (!previewHtmlString || frame === null || frame.contentWindow === null)
      return;

    frame.contentWindow.document.open("text/html", "replace");
    frame.contentWindow.document.write(previewHtmlString);
    frame.contentWindow.document.close();
    darkWasSet.value = false;
  }

  watch(darkMode, (newValue) => {
    if (newValue) {
      setDarkMode();
    } else {
      setLightMode();
    }
  });

  watch(langToShow, () => {
    darkWasSet.value = false;
  });

  onMounted(async () => {
    previewStore.setPreviewHtml(null);
    darkMode.value = false;

    handleResize();

    setPreview();

    // const iframeElement = document.getElementById("previewIframe");
    // if (iframeElement) {
    //   iframeElement.addEventListener("load", onIframeLoad);
    // }
    window.addEventListener("resize", handleResize);
    window.$event.on("setHtml", setHtml);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("resize", handleResize);
    // const iframeElement = document.getElementById("previewIframe");
    // if (iframeElement) {
    //   iframeElement.removeEventListener("load", onIframeLoad);
    // }
    window.$event.off("setHtml", setHtml);
  });
</script>
